<template>
  <div class="content">
    <div class="md-layout">
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-75">
        <md-card>
          <md-card-header data-background-color="purple">
            <div class="md-layout md-alignment-center-space-between">
              <h4 class="title">Logs</h4>
              <div style="display: flex">
                <table-settings
                  v-if="colsReady"
                  :minimized="true"
                  :cols="availableCols"
                  :default-cols="defaultCols"
                  :force-close="searchOpened"
                  :availableLogColumnsToggle="availableLogColumnsToggle"
                  @open="settingsOpened = true"
                  @close="settingsOpened = false"
                  @saved="filterLogCols"
                  @resetDefault="resetDefaultCols"
                  @changeAvailableLogColumnsToggle="availableLogColumnsToggle = !availableLogColumnsToggle"
                ></table-settings>
                <vue-select-search
                  @doSearch="doSearch"
                  :operands="availableCols"
                  :operators="['==', '>=', '<=', '!=', 'contains']"
                  :force-close="settingsOpened"
                  @open="searchOpened = true"
                  @close="searchOpened = false"
                ></vue-select-search>
                <md-button @click="refreshLogs()" class="md-icon-button md-dense refresh-btn">
                  <md-tooltip md-direction="top">Refresh logs</md-tooltip>
                  <md-icon>refresh</md-icon>
                </md-button>
              </div>
            </div>
          </md-card-header>
          <md-card-content>
            <div v-if="waitingForServerResponse">
              <md-empty-state
                md-icon="file_download"
                md-label="Loading..."
                md-description="We are trying to filter audit logs using your query."
                class="md-table-empty-state"
              >
                <loading-three-dots></loading-three-dots>
              </md-empty-state>
            </div>
            <div v-else>
              <md-table v-model="logs" table-header-color="purple" md-card>
                <md-table-empty-state
                  md-icon="warning"
                  md-label="No audit log found!"
                  md-description="No audit log found for your query. Please change query for results."
                >
                </md-table-empty-state>

                <md-table-row slot="md-table-row" slot-scope="{ item }">
                  <md-table-cell v-for="label in Object.keys(item)" :md-label="label" :key="label"
                    >{{ item[label] }}
                  </md-table-cell>
                </md-table-row>
              </md-table>
            </div>

            <!-- Pagination -->
            <div class="md-layout md-alignment-center">
              <pagination v-model="logsPage" :pageCount="10000000" @input="changePage"></pagination>
            </div>
          </md-card-content>
        </md-card>
      </div>
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-25">
        <filter-card @submit="refreshLogs" />
      </div>
    </div>
  </div>
</template>

<script>
import { FilterCard, Pagination, TableSettings, VueSelectSearch } from "@/components";
import LoadingThreeDots from "@/components/LoadingThreeDots";
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import apiManager from "../api-manager";
import { createSearchQuery } from "@/utils/queryPyConverter";
import moment from "moment";
import fetchErrorHandler from "../error";

export default {
  name: "Audit",
  components: { TableSettings, VueSelectSearch, FilterCard, Pagination, LoadingThreeDots },
  setup() {
    const filterCols = (cols, array) => {
      return array.map((item) => {
        let result = {};
        cols.forEach((col) => {
          result[col] = item[col];
        });
        return result;
      });
    };

    const logs = ref([]);
    let cachedLogs = [];
    const getDefaultCols = () => {
      const cols = window.localStorage.getItem("auditCols");
      return cols === null ? ["DATE", "TIME", "LEVEL", "METHOD", "SRC", "URL", "STAT", "USER"] : cols.split(",");
    };
    const defaultCols = ref(getDefaultCols());
    const getLogs = async (filter = null, page = 1) => {
      const response = await apiManager.getAuditLogs({
        params: {
          filter: filter,
          page: page,
        },
      });

      if (response.status === 200) return response.parsedData.logs;
      else {
        fetchErrorHandler(response.status, response.statusText, "Audit");
        return [];
      }
    };

    const getAvailableCols = (array, currentCols = []) => {
      const result = [...currentCols];
      for (const item of array) result.push(...Object.keys(item));
      return [...new Set(result)];
    };
    const availableCols = ref([]);

    const localizeDate = (dateTime) => moment.utc(dateTime).local().format("YYYY-MM-DD");
    const localizeTime = (dateTime) => moment.utc(dateTime).local().format("HH:mm:ss");

    const waitingForServerResponse = ref(false);
    const refreshLogs = async (filter = null, page = 1) => {
      waitingForServerResponse.value = true;

      cachedLogs = (await getLogs(filter, page)).map((item) => {
        const dateTime = `${item.DATE} ${item.TIME}`;
        return {
          ...item,
          ...{ DATE: localizeDate(dateTime.slice(0, dateTime.search(","))) },
          TIME: localizeTime(dateTime.slice(0, dateTime.search(","))),
        };
      });

      changeAvailableLogColumnsToggle();
      waitingForServerResponse.value = false;
    };
    const resetDefaultCols = async () => {
      window.localStorage.removeItem("auditCols");
      defaultCols.value = getDefaultCols();
      await refreshLogs();
    };

    const filterLogCols = async (event) => {
      window.localStorage.setItem("auditCols", event);
      logs.value = filterCols(event, cachedLogs);
      defaultCols.value = event;
    };
    const colsReady = computed(() => {
      return Boolean(availableCols.value.length);
    });
    const settingsOpened = ref(false);
    onMounted(async () => {
      refreshLogs();
    });

    const cachedSearchList = ref([]);
    const doSearch = (searchList, page = 1) => {
      cachedSearchList.value = searchList;
      logsPage.value = page;

      const searchQuery = searchList.length ? createSearchQuery(searchList) : null;
      refreshLogs(searchQuery, logsPage.value);
    };
    const searchOpened = ref(false);

    const logsPage = ref(1);
    const changePage = (page) => {
      doSearch(cachedSearchList.value, page);
    };
    const getAvailableLogColumnsToggleStorage = () => {
      const cachedAvailableLogColumnsToggle = window.localStorage.getItem("availableLogColumnsToggle");
      return cachedAvailableLogColumnsToggle === "true";
    };
    const availableLogColumnsToggle = ref(getAvailableLogColumnsToggleStorage());
    const changeAvailableLogColumnsToggle = () => {
      availableCols.value = getAvailableCols(cachedLogs, availableCols.value);
      if (!availableLogColumnsToggle.value) {
        logs.value = filterCols(defaultCols.value, cachedLogs);
      } else {
        logs.value = cachedLogs;
      }
    };

    watch(availableLogColumnsToggle, () => {
      changeAvailableLogColumnsToggle();
    });

    return {
      logs,

      waitingForServerResponse,
      refreshLogs,
      filterLogCols,
      resetDefaultCols,
      availableCols,
      defaultCols,
      colsReady,
      settingsOpened,

      cachedSearchList,
      doSearch,
      searchOpened,

      // Pagination
      logsPage,
      changePage,
      availableLogColumnsToggle,
      changeAvailableLogColumnsToggle,
    };
  },
};
</script>

<style scoped></style>
